/**
 * Building one aircraft's renderable mesh from a baked `.TBL`. Shared by the
 * player's aeroplane and every opponent, so the two cannot drift apart in how
 * they are centred, oriented or shaded.
 *
 * Two corrections the raw geometry needs, both documented in
 * `docs/formats/models-tbl.md`: the `.TBL` origin is not the centre of the
 * airframe (CAMEL's is up by the engine), so the mesh is recentred inside its
 * pose group or the chase camera frames empty air; and baked models are Z-up,
 * so a −90° pitch about X puts them in the engine's Y-up frame.
 *
 * **Shading is per frame, not per bake.** An aeroplane's faces walk their
 * `BARON.PAL` ramps as it banks (`three/ramp-material.ts`, implementing
 * docs/formats/shading-and-paint.md §2/§3), which is the one thing the baked
 * `colorRgba` cannot express: it is resolved at a single reference attitude, so
 * a mesh drawn with it alone keeps a rolling aeroplane lit as though it were
 * straight and level. Scenery deliberately does *not* go through here — it
 * bakes flat, measured off the running original (§9.3).
 *
 * **Who owns what.** Each mesh gets its own material, exactly as this module
 * always did, so `three/dispose-tree.ts` keeps freeing everything a torn-down
 * scene owns without knowing the ramp exists. The two *lookup textures* are
 * shared and deliberately outlive any one scene: they are 8 KB of `BARON.PAL`
 * and `RB.EXE` shade deltas, identical for every aeroplane in every mission, and
 * a per-scene copy would mean re-fetching the palette on every return to `/fly`.
 * `material.dispose()` does not touch a material's textures, so the shared pair
 * stays valid across as many flights as the tab lives through.
 *
 * ## ENHANCED MODELS
 *
 * This is also the seam the ENHANCED MODELS switch turns
 * (`effects-settings.ts`, `docs/engineering/enhanced-models.md`). With it on, an
 * airframe the offline tool has built a `.glb` for is drawn from that instead of
 * from its baked `.TBL`; every other airframe in the same sky is untouched, so a
 * fleet where one type is enhanced and three are not is the normal case rather
 * than a broken one. Three things keep the swap honest:
 *
 * - **the aeroplane does not move.** An enhanced model is longer than its
 *   authentic twin (it has a propeller, which the original has not), so its own
 *   bounding box is the wrong thing to centre it by — the sim's gun breeches and
 *   cockpit datum are measured against the *baked* recentring
 *   (`aircraft-model-frame.ts`, `MODEL_CENTRE_UNITS`). Both paths therefore take
 *   the same number out of `centredBakedGeometry`, and the enhanced one carries
 *   it on the object rather than in the geometry;
 * - **the propeller hub stays baked.** `propellerHubOf` measures the authentic
 *   LOD whatever is on screen, so AD-012's disc sits in the same place in both
 *   states — and where that disc is drawn, the `.glb`'s own modelled blade is
 *   hidden, because two propellers on one nose is what the enhanced model looked
 *   like until 2026-08-24 (`modelled-propeller.ts` has the rule and the three
 *   airframes it exempts);
 * - **paint is never guessed.** An ace flies a *different model id*
 *   (`models/FOKDR1@Richthofen`), so his enhanced aeroplane is a different `.glb`
 *   and the index either has it or it does not. A pilot's own paint-shop
 *   colours are applied to the enhanced model's own `.CLG` groups
 *   (`repaintEnhanced`), and if that model carries no groups to move, the load
 *   falls back to the baked aeroplane rather than showing factory colours the
 *   pilot did not choose.
 */

import { personalSchemeColours, type PersonalPaint } from "@red-opus/formats";
import { propellerHubOrFallback, type PropellerHub } from "@red-opus/baker/schema";
import * as THREE from "three/webgpu";
import { bestRenderableLod, createLodGeometry, loadBakedModel } from "../three/baked-model.ts";
import { enhancedIndexOnce, enhancedLadder } from "../three/enhanced-catalog.ts";
import { warnEnhancedOnce } from "../three/enhanced-diagnostics.ts";
import { enhancedLodOf } from "../three/enhanced-lod.ts";
import { applyMaterial, disposeEnhancedModel, loadEnhancedRungs, repaintEnhanced } from "../three/enhanced-model.ts";
import { setModelledPropellerVisible } from "../three/enhanced-nodes.ts";
import type { BytesProgress } from "../three/streamed-body.ts";
import { modelledPropellerVisible } from "./modelled-propeller.ts";
import { repaintLod, type PaintableLod } from "../three/model-repaint.ts";
import type { CockpitInterior } from "../three/cockpit-interior.ts";
import { createRampMaterial, createRampTables, SHADING_PALETTE_PATH, type RampTables } from "../three/ramp-material.ts";
import type { BakedPaletteBody } from "../three/ramp-shade.ts";
import { CAMERA_FOV_DEG } from "./flight-view.ts";
import { MODEL_TO_WORLD_PITCH_RAD } from "./world.ts";

/** `data/baked/models/<id>.json` path for a baked model id, as `/baked/` serves it. */
export function bakedModelPath(id: string): string {
  return `${id}.json`;
}

let sharedTables: Promise<RampTables | undefined> | undefined;
let sharedPalette: Promise<BakedPaletteBody | undefined> | undefined;
const sharedHubs = new Map<string, Promise<PropellerHub | undefined>>();

/**
 * Where this model's propeller disc goes, measured off the same baked LOD the
 * aeroplane is drawn from (`packages/baker/src/propeller-hub.ts`, AD-012).
 *
 * Memoised per model id for the life of the document, the same way the ramp
 * tables are: a hub is a constant of the *airframe*, so a mission with four
 * types measures four hubs however many aeroplanes are in the sky and a return
 * to `/fly` measures none at all. `undefined` means the model could not be
 * fetched, which costs that airframe its disc and nothing else — the same
 * "skip what fails to load" policy `aircraftMaterial` applies to the palette.
 */
export function propellerHubOf(modelId: string): Promise<PropellerHub | undefined> {
  const existing = sharedHubs.get(modelId);
  if (existing) return existing;
  const pending = loadBakedModel(bakedModelPath(modelId))
    .then((body) => {
      const best = bestRenderableLod(body.lods);
      return best ? propellerHubOrFallback(best) : undefined;
    })
    .catch(() => undefined);
  sharedHubs.set(modelId, pending);
  return pending;
}

/** Fetches `BARON.PAL` once per document; `undefined` means it did not load. */
export function shadingPalette(): Promise<BakedPaletteBody | undefined> {
  sharedPalette ??= fetch(SHADING_PALETTE_PATH)
    .then((response) => (response.ok ? (response.json() as Promise<BakedPaletteBody>) : undefined))
    .catch(() => undefined);
  return sharedPalette;
}

/** Builds the shared lookup pair from that palette, once per document. */
function rampTables(): Promise<RampTables | undefined> {
  sharedTables ??= shadingPalette().then((palette) => (palette ? createRampTables(palette) : undefined));
  return sharedTables;
}

/**
 * The material one aeroplane draws with.
 *
 * If `BARON.PAL` cannot be fetched the aeroplane still draws — in its baked
 * reference-attitude colours, through the plain unlit material this replaced.
 * That is the region loader's "skip what fails to load" precedent applied to a
 * visual refinement: a missing palette should cost the re-shading, not the
 * aeroplane.
 */
async function aircraftMaterial(interior: CockpitInterior | undefined): Promise<THREE.Material> {
  const tables = await rampTables();
  return tables ? createRampMaterial(tables, interior) : new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
}

/**
 * One baked LOD's geometry with the `.TBL` origin taken out of it, plus the
 * offset that was removed.
 *
 * The offset is the interesting half. A `.TBL`'s origin is not the centre of the
 * airframe — CAMEL's is up by the engine — and everything the sim measures
 * against the drawn aeroplane (`aircraft-model-frame.ts`) is measured against
 * this recentring. So it is computed in exactly one place and both the baked and
 * the enhanced path use the number that comes out.
 */
function centredBakedGeometry(lod: PaintableLod): { geometry: THREE.BufferGeometry; centre: THREE.Vector3 } {
  const geometry = createLodGeometry(lod);
  geometry.computeBoundingBox();
  const centre = new THREE.Vector3();
  geometry.boundingBox?.getCenter(centre);
  geometry.translate(-centre.x, -centre.y, -centre.z);
  return { geometry, centre };
}

/**
 * The enhanced `.glb` for `modelId`, placed and painted to stand exactly where
 * the baked aeroplane would, or `undefined` for every reason to use the baked
 * one instead: no enhanced model exists for this id, the fetch failed, or the
 * pilot's paint could not be applied to it.
 *
 * `centre` arrives in the *model* frame; the `.glb` is already in the world's,
 * through the same `(x, y, z) -> (x, z, -y)` the baked mesh's own -90° pitch
 * applies, so the offset is turned by that transform before it is used.
 */
async function enhancedAircraft(
  modelId: string,
  centre: THREE.Vector3,
  wanted: readonly number[] | undefined,
  palette: BakedPaletteBody | undefined,
  ladder: LodLadderOptions | undefined,
  /** The caller's own options, for the three this path reads: `propellerEffect`, `onBytes` and `cockpitInterior`. */
  options: AircraftMeshOptions,
): Promise<THREE.Object3D | undefined> {
  const entry = (await enhancedIndexOnce()).get(modelId);
  // Not in the index is the ordinary case — fifteen of the twenty-two scouts
  // have no enhanced model and take the baked path in both toggle states — so
  // it is the one branch here that says nothing.
  if (entry === undefined) return undefined;
  // `entry.bytes` is the manifest's decoded length, and it is the loading bar's
  // denominator: since G48 the fleet is served brotli, so `Content-Length` is
  // the compressed number and the reader hands over decoded bytes (G58).
  const loaded = await loadEnhancedRungs(entry.path, options.onBytes, entry.bytes).catch((error: unknown) => {
    // In the index and yet unloadable is never ordinary: the manifest and the
    // models are written by the same command, so the file should be there.
    warnEnhancedOnce(`${modelId} is in the manifest but its model would not load — drawing the baked .TBL instead. ${String(error)}`);
    return undefined;
  });
  if (!loaded || loaded.length === 0) return undefined;
  // The rungs this aeroplane will actually draw. Without a ladder that is the
  // finest one alone, and the rest are freed **before** anything is shared with
  // them — `disposeEnhancedModel` frees materials, and they are about to be
  // given the same material instance as the rung that survives.
  const rungs = ladder ? loaded : loaded.slice(0, 1);
  for (const rung of loaded.slice(rungs.length)) disposeEnhancedModel(rung);
  // Paint goes on **every** drawn rung, not just the one on screen when the
  // aeroplane loads: a rung that is not repainted is an aeroplane that changes
  // colour as it flies away.
  if (wanted && !rungs.every((rung) => palette !== undefined && repaintEnhanced(rung, wanted, palette))) {
    warnEnhancedOnce(
      `${modelId}'s enhanced model carries no paint groups to move (or BARON.PAL did not load), so the pilot's own colours ` +
        "cannot be applied to it — drawing the baked .TBL rather than showing paint he did not choose. Rebuild with `bun run enhanced`.",
    );
    for (const rung of rungs) disposeEnhancedModel(rung);
    return undefined;
  }
  const material = await aircraftMaterial(options.cockpitInterior);
  for (const rung of rungs) applyMaterial(rung, material);
  // One propeller, not two. The `.glb` carries the blades the original drew as
  // line nodes; AD-012's effect draws its own over the same hub in every regime.
  // Whichever is on screen, only one of them is (`modelled-propeller.ts`).
  const showBlade = modelledPropellerVisible(modelId, options.propellerEffect ?? false);
  for (const rung of rungs) setModelledPropellerVisible(rung, showBlade);
  const object = ladder ? enhancedLodOf(rungs, enhancedLadder(), ladder.boundingRadiusM, ladder.fovYRadians) : rungs[0];
  if (!object) return undefined;
  object.position.set(-centre.x, -centre.z, centre.y);
  return object;
}

/** What `enhancedLodOf` needs to turn the manifest's apparent-size thresholds into camera distances. */
interface LodLadderOptions {
  readonly boundingRadiusM: number;
  readonly fovYRadians: number;
}

export interface AircraftMeshOptions {
  /**
   * The player's own wings/fuselage choices, expanded against whatever factory
   * scheme the entry was baked in. The bake ships the factory scheme and each
   * ace's as whole entries, but the player's two paint-shop choices make 64
   * combinations per airframe, so his are applied at load — the same load-time
   * repaint the original does (`three/model-repaint.ts`).
   */
  readonly paint?: PersonalPaint;
  /** ENHANCED MODELS: draw the offline-built `.glb` for this airframe when one exists. */
  readonly enhanced?: boolean;
  /**
   * Let this aeroplane drop to a coarser enhanced rung as it gets further away
   * (`three/enhanced-lod.ts`).
   *
   * **Off by default, and that is the interesting half.** Every caller that
   * shows one aeroplane at a size it chose — the `/assets` comparison, the
   * career turntable, the plane viewer, and the player's own machine, which the
   * chase camera keeps within twenty metres — wants the finest rung and nothing
   * else. The fleet is the only place a distance is the *sim's* to decide, so
   * `combat-fleet.ts` is the only caller that asks for a ladder.
   */
  readonly lodLadder?: boolean;
  /**
   * Whether AD-012's propeller effect is running on the screen this aeroplane is
   * being loaded for — **not** whether the setting is on.
   *
   * It decides one thing: whether the enhanced `.glb` draws the propeller it was
   * built with (`modelled-propeller.ts`). Two propellers on one nose is the bug
   * this exists to stop, and the caller is the only one that knows whether there
   * is an effect layer in its scene at all.
   *
   * **Defaults to false**, i.e. "draw the blades", because the callers with no
   * effect layer — the career turntable, and anything else showing an aeroplane
   * on a stand — are the ones that would otherwise have to remember. `/fly` and
   * the plane viewer both own a `PropellerDiscs` and both pass their setting.
   */
  readonly propellerEffect?: boolean;
  /**
   * 3D COCKPIT's interior treatment, for the **player's own** aeroplane only
   * (`three/cockpit-interior.ts`).
   *
   * Passed by exactly one caller — `scene-boot.ts`'s `loadAircraft` — and
   * omitted by every other, which is the point: the extra nodes exist on one
   * material in the whole scene, so the fleet's shaders are byte-for-byte what
   * they were. It is not gated on the *setting* here, because the setting can be
   * flipped mid-flight with no reload; the gate is a uniform inside it, and at
   * rest it returns the base cosine exactly.
   */
  readonly cockpitInterior?: CockpitInterior;
  /**
   * Called while the enhanced `.glb` is still arriving, with the bytes read so
   * far and the length the response declared.
   *
   * Only `/fly`'s boot passes one, and only because that fetch is the largest
   * single thing a cold flight downloads: it is what lets the loading bar report
   * a *measurement* instead of easing along the mean of two reference runs
   * (`flight/boot-progress.ts`). Nothing else here changes — the baked path
   * never calls it, and a caller that omits it gets the one-shot read it always
   * got.
   */
  readonly onBytes?: BytesProgress;
}

/** Loads `modelId` as a centred, world-oriented, ramp-shaded aeroplane — enhanced when asked for and available, baked otherwise. */
export async function loadAircraftMesh(modelId: string, options: AircraftMeshOptions = {}): Promise<THREE.Object3D | undefined> {
  const body = await loadBakedModel(bakedModelPath(modelId));
  const best = bestRenderableLod(body.lods);
  if (!best) return undefined;
  const factory = body.paint?.colours;
  const wanted = options.paint && factory ? personalSchemeColours(factory, options.paint) : undefined;
  const palette = wanted ? await shadingPalette() : undefined;
  const lod = wanted && palette ? repaintLod(best, wanted, palette) : best;

  const { geometry, centre } = centredBakedGeometry(lod);
  if (options.enhanced) {
    // The switching distances are a function of how big the object is, and the
    // authoritative size is the bake's own `boundingRadius` — the same number
    // the sprite ladder measures apparent size against, so an enhanced
    // aeroplane and a baked one of the same type change rung at the same range.
    const ladder = options.lodLadder ? { boundingRadiusM: body.boundingRadius, fovYRadians: (CAMERA_FOV_DEG * Math.PI) / 180 } : undefined;
    const enhanced = await enhancedAircraft(modelId, centre, wanted, palette, ladder, options);
    if (enhanced) {
      geometry.dispose();
      return enhanced;
    }
  }

  const mesh = new THREE.Mesh(geometry, await aircraftMaterial(options.cockpitInterior));
  mesh.rotation.x = MODEL_TO_WORLD_PITCH_RAD;
  return mesh;
}
